import CloseIcon from "@mui/icons-material/Close"
import { Box, Dialog, styled, Typography } from "@mui/material"
import IconButton from "@mui/material/IconButton"
import { ics } from "calendar-link"
import QRCode from "components/common/QRCode"
import TicketsCount from "components/conventions/TicketsCount"
import { useExpoCopyText } from "hooks/useExpoCopyText"
import { useUser } from "hooks/useUser"
import getConventionAddress from "lib/conventions/getConventionAddress"
import createGoogleMapLink from "lib/google-maps/createGoogleMapLink"
import Convention from "models/convention-api/v1/Convention"
import ConventionRegistration from "models/convention-api/v1/ConventionRegistration"
import Link from "next/link"
import React from "react"
import { pxToRem } from "utils/helpers"

interface ShowTicketsDialogProps {
  convention: Convention
  conventionRegistration: ConventionRegistration
  onClose: () => void
}

const ShowTicketsDialog = (props: ShowTicketsDialogProps) => {
  const { convention, conventionRegistration, onClose } = props
  const user = useUser()
  const expoCopyText = useExpoCopyText()

  const startAt = convention.start_at.tz(convention.time_zone)
  const endAt = convention.end_at.tz(convention.time_zone)

  const formattedDate = (() => {
    const startAtDate = startAt.format("MMM DD, YYYY")
    const endAtDate = endAt.format("MMM DD, YYYY")

    const startAtTime = startAt.format("h:mm a")
    const endAtTime = endAt.format("h:mm a z")

    const isSameDay = startAtDate === endAtDate

    return (
      <_DateTime component="time" dateTime={`${startAt}/${endAt}`}>
        {isSameDay && (
          <>
            {startAtDate}
            <br />
            {startAtTime} - {endAtTime}
          </>
        )}
        {!isSameDay && (
          <>
            {startAtDate} {startAtTime} - {endAtDate} {endAtTime}
          </>
        )}
      </_DateTime>
    )
  })()

  const handleClose = () => onClose()

  const googleMapHref =
    convention.latitude &&
    convention.longitude &&
    createGoogleMapLink({
      latitude: convention.latitude,
      longitude: convention.longitude,
    })

  const ICSHref = ics({
    title: convention.name,
    start: startAt.toISOString(),
    end: endAt.toISOString(),
  })

  return (
    <_Dialog open onClose={handleClose}>
      <_CloseIconButton
        color="dark"
        onClick={handleClose}
        aria-label={`Close ${convention.name} tickets dialog`}>
        <CloseIcon />
      </_CloseIconButton>
      <_Container>
        <_QRCode value={user!.qr_code} ratio={1.945} />
        <TicketsCount convention={convention} conventionRegistration={conventionRegistration} />

        <_SectionContainer>
          <_SectionName>Name</_SectionName>
          <Typography variant="header2" textTransform="capitalize">
            {user.first_name} {user.last_name}
          </Typography>
        </_SectionContainer>

        <_SectionContainer>
          <_SectionName>{expoCopyText?.singular}</_SectionName>
          <Typography variant="header4">{convention.name}</Typography>
        </_SectionContainer>

        <_SectionContainer>
          <_SectionName>Date</_SectionName>
          {formattedDate}
          {ICSHref && (
            <Link
              rel="noopener"
              target="_blank"
              href={ICSHref}
              aria-label={`Add ${convention.name} ${expoCopyText!.singular} to your calendar`}>
              ADD TO CALENDAR
            </Link>
          )}
        </_SectionContainer>

        <_SectionContainer>
          <_SectionName>Location</_SectionName>
          <Typography variant="paragraphRegular">{convention.location_name}</Typography>
          <Typography variant="paragraphRegular">{getConventionAddress(convention)}</Typography>
          {googleMapHref && (
            <Link
              href={googleMapHref}
              rel="noopener"
              target="_blank"
              aria-label={`View ${convention.name} ${expoCopyText!.singular} on a map`}>
              VIEW MAP
            </Link>
          )}
        </_SectionContainer>
      </_Container>
    </_Dialog>
  )
}

export default ShowTicketsDialog

const _Dialog = styled(Dialog)(({ theme }) => ({
  "& > .MuiDialog-container > .MuiDialog-paper": {
    position: "relative",

    width: "100%",
    maxWidth: pxToRem(460),

    borderRadius: pxToRem(10),

    backgroundColor: theme.palette.primary.lightest,
    boxShadow: `0 ${pxToRem(4)} ${pxToRem(4)} rgba(0, 0, 0, 0.25), 0 0 ${pxToRem(
      4,
    )} rgba(0, 0, 0, 0.12)`,

    overflow: "hidden",
  },
})) as typeof Dialog

const _Container = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: pxToRem(25),
  margin: pxToRem(20),

  overflowY: "auto",
  "&::-webkit-scrollbar": {
    display: "none",
  },

  [theme.breakpoints.up("md")]: {
    margin: pxToRem(25),
  },
})) as typeof Box

const _QRCode = styled(QRCode)(({}) => ({
  display: "flex",
  justifyContent: "center",
  width: "100%",
  marginTop: pxToRem(10),
  marginBottom: pxToRem(20),
})) as typeof QRCode

const _SectionContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: pxToRem(5),
  textAlign: "center",
})) as typeof Box

const _SectionName = styled(Typography)(({ theme }) => ({
  marginBottom: pxToRem(5),
  ...theme.typography.paragraphRegular,
  color: theme.palette.gray.darker,
  textTransform: "capitalize",
})) as typeof Typography

const _DateTime = styled(Typography)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  flexDirection: "column",
  gap: pxToRem(5),
  ...theme.typography.paragraphRegular,
})) as typeof Typography

const _CloseIconButton = styled(IconButton)(() => ({
  position: "absolute",
  top: pxToRem(5),
  right: pxToRem(5),
}))
